import { useEffect, useRef } from 'react';
import { useTabAlertStore } from './tab-alert-store.js';
import { ws } from './ws.js';

/** Путь к обычному favicon */
const DEFAULT_FAVICON = '/icons/favicon-32x32.png';

/** Интервал мигания favicon (мс) */
const BLINK_INTERVAL = 800;

/**
 * Генерирует favicon с красным badge-счётчиком через canvas.
 * Возвращает data URL.
 */
function createBadgeFavicon(count: number): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Рисуем оригинальный favicon
      ctx.drawImage(img, 0, 0, 32, 32);

      // Красный круг в правом верхнем углу
      const badgeRadius = 10;
      const badgeX = 32 - badgeRadius;
      const badgeY = badgeRadius;

      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeRadius, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();

      // Белая обводка
      ctx.strokeStyle = '#18181b';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Число или "!" если много
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const text = count > 9 ? '!' : String(count);
      ctx.fillText(text, badgeX, badgeY + 1);

      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      // Fallback: просто красный круг
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(16, 16, 14, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 16px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('!', 16, 17);

      resolve(canvas.toDataURL('image/png'));
    };
    img.src = DEFAULT_FAVICON;
  });
}

/**
 * Устанавливает favicon по href.
 */
function setFavicon(href: string): void {
  // Ищем существующий link[rel="icon"] размера 32x32 или первый попавшийся
  let link = document.querySelector<HTMLLinkElement>(
    'link[rel="icon"][sizes="32x32"]'
  );
  if (!link) {
    link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  }
  if (link) {
    link.href = href;
  }
}

/**
 * Хук для анимации вкладки браузера при новых permission_request.
 *
 * - Слушает WS на `permission_request`
 * - Если документ скрыт — увеличивает счётчик
 * - Если документ виден — сбрасывает счётчик
 * - При счётчике > 0 — мигает favicon и добавляет `(N)` к title
 *
 * Вызывать один раз в App.tsx (внутри авторизованной зоны).
 */
export function useTabAlert(): void {
  const alertCount = useTabAlertStore((s) => s.alertCount);
  const baseTitle = useTabAlertStore((s) => s.baseTitle);
  const increment = useTabAlertStore((s) => s.increment);
  const reset = useTabAlertStore((s) => s.reset);

  const blinkIntervalRef = useRef<number | null>(null);
  const badgeFaviconRef = useRef<string | null>(null);
  const isBlinkOnRef = useRef(false);

  // Слушаем WS на permission_request
  useEffect(() => {
    const unsub = ws.onMessage((msg) => {
      if (msg.type === 'permission_request') {
        increment();
      }
    });
    return unsub;
  }, [increment]);

  // Слушаем visibilitychange — сбрасываем счётчик когда вкладка видима
  useEffect(() => {
    const handler = () => {
      if (!document.hidden) {
        reset();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [reset]);

  // Синхронизируем document.title
  useEffect(() => {
    if (alertCount > 0) {
      document.title = `(${alertCount}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [alertCount, baseTitle]);

  // Анимация favicon
  useEffect(() => {
    if (alertCount > 0) {
      // Генерируем badge favicon
      void createBadgeFavicon(alertCount).then((dataUrl) => {
        badgeFaviconRef.current = dataUrl;
      });

      // Запускаем мигание
      if (blinkIntervalRef.current === null) {
        isBlinkOnRef.current = true;
        blinkIntervalRef.current = window.setInterval(() => {
          isBlinkOnRef.current = !isBlinkOnRef.current;
          if (isBlinkOnRef.current && badgeFaviconRef.current) {
            setFavicon(badgeFaviconRef.current);
          } else {
            setFavicon(DEFAULT_FAVICON);
          }
        }, BLINK_INTERVAL);

        // Сразу показываем badge
        void createBadgeFavicon(alertCount).then((dataUrl) => {
          setFavicon(dataUrl);
        });
      } else {
        // Обновляем badge с новым числом
        void createBadgeFavicon(alertCount).then((dataUrl) => {
          badgeFaviconRef.current = dataUrl;
          if (isBlinkOnRef.current) {
            setFavicon(dataUrl);
          }
        });
      }
    } else {
      // Останавливаем анимацию
      if (blinkIntervalRef.current !== null) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
      }
      setFavicon(DEFAULT_FAVICON);
      badgeFaviconRef.current = null;
    }

    return () => {
      if (blinkIntervalRef.current !== null) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
      }
    };
  }, [alertCount]);
}
